import { Observable } from 'rxjs/Rx'
import { combineEpics } from 'redux-observable'
import { update, bank, getCompany, getCompanies } from 'store/services/company'
import { fetchRequest as fetchProfileRequest } from 'store/ducks/profile'
import { alertSuccess, alertErrorDefault } from 'store/ducks/alert'
import * as authenticationDuck from 'store/ducks/authentication'
import atob from 'store/utils/atob'
import { TYPES } from 'constants/user'
import { intl } from 'i18n'
import { Violation } from 'interfaces/common'
import { Company } from 'interfaces/company'
import { GlobalState } from './state'
import { ListenerEffectAPI, createListenerMiddleware, Dispatch } from '@reduxjs/toolkit'

const FIELDS = [
  'name',
  'email',
  'vatIdentificationNumber',
  'streetAddress',
  'postalCode',
  'city',
  'country',
  'legalRepresentativeFirstName',
  'legalRepresentativeLastName',
  'legalRepresentativeBirthDay',
  'legalRepresentativeNationality',
  'legalRepresentativeCountryOfResidence',
  'legalRepresentativeEmail',
  'legalRepresentativeStreetAddress',
  'legalRepresentativeAdditionalStreetAddress',
  'legalRepresentativePostalCode',
  'legalRepresentativeCity',
  'legalRepresentativeCountry',
  'headquartersStreetAddress',
  'headquartersAdditionalStreetAddress',
  'headquartersPostalCode',
  'headquartersCity',
  'headquartersCountry',
  'iban',
  'bankInformationFullName',
  'bankInformationStreetAddress',
  'bankInformationAdditionalStreetAddress',
  'bankInformationPostalCode',
  'bankInformationCity',
  'bankInformationCountry',
  'mangoMandateStatus',
]

const DOCUMENTS = ['statute', 'legalRepresentativeProofOfIdentity', 'proofOfRegistration', 'shareholderDeclaration']

class DataTransformer {
  static transform(payload) {
    return {
      ...payload,
    }
  }

  static reverseTransform(payload) {
    return {
      ...payload,
    }
  }
}

// Actions
export const UPDATE_REQUEST = 'company/UPDATE_REQUEST'
export const UPDATE_SUCCESS = 'company/UPDATE_SUCCESS'
export const UPDATE_FAILURE = 'company/UPDATE_FAILURE'

export const UPDATE_BANK_REQUEST = 'company/UPDATE_BANK_REQUEST'
export const UPDATE_BANK_SUCCESS = 'company/UPDATE_BANK_SUCCESS'
export const UPDATE_BANK_FAILURE = 'company/UPDATE_BANK_FAILURE'

export const FETCH_COMPANY = 'company/FETCH_COMPANY'
export const FETCH_COMPANY_SUCCESS = 'company/FETCH_COMPANY_SUCCESS'
export const FETCH_COMPANY_FAILURE = 'company/FETCH_COMPANY_FAILURE'

export const FETCH_COMPANIES = 'company/FETCH_COMPANIES'
export const FETCH_COMPANIES_SUCCESS = 'company/FETCH_COMPANIES_SUCCESS'
export const FETCH_COMPANIES_FAILURE = 'company/FETCH_COMPANIES_FAILURE'
export const UPDATE_COMPANY_SEARCH = 'companu/UPDATE_COMPANY_SEARCH'

export type CompanyAction = Record<string, unknown>
export type CompanyState = {
  edit: {
    errors: string[]
    loading: boolean
    uploaded: boolean
    violations: Violation[]
    initialValues?: Record<string, unknown>
  }
  company?: {
    entity: Company | Record<string, never>
    loading: boolean
  }
  companies?: {
    entities: Company[] | Record<string, never>
    loading: boolean
  }
  search: string
}

// Reducer
const INITIAL_STATE = {
  edit: {
    loading: false,
    uploaded: false,
    errors: [],
    violations: [],
  },
  company: {
    loading: false,
    entity: undefined,
  },
  companies: {
    loading: false,
    entities: undefined,
  },
  search: '',
}

export function reducer(state: CompanyState = INITIAL_STATE, action: CompanyAction) {
  switch (action.type) {
    case UPDATE_REQUEST:
      return {
        ...state,
        edit: {
          ...INITIAL_STATE.edit,
          initialValues: { ...state.edit.initialValues },
          loading: true,
          uploaded: false,
          errors: [],
        },
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        edit: {
          ...state.edit,
          loading: false,
          uploaded: true,
          errors: [],
        },
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        edit: {
          ...state.edit,
          loading: false,
          uploaded: false,
          errors: action.errors,
          violations: action.violations || [],
        },
      }
    case UPDATE_BANK_REQUEST:
      return {
        ...state,
        edit: {
          ...INITIAL_STATE.edit,
          initialValues: { ...state.edit.initialValues },
          loading: true,
        },
      }
    case UPDATE_BANK_SUCCESS:
      return {
        ...state,
        edit: {
          ...state.edit,
          loading: false,
        },
      }
    case UPDATE_BANK_FAILURE:
      return {
        ...state,
        edit: {
          ...state.edit,
          loading: false,
          violations: action.violations || [],
        },
      }

    case FETCH_COMPANY:
      return {
        ...state,
        company: {
          ...state.company,
          loading: true,
        },
      }
    case FETCH_COMPANY_SUCCESS:
      return {
        ...state,
        company: {
          entity: action.entity,
          loading: false,
        },
      }
    case FETCH_COMPANY_FAILURE:
      return {
        ...state,
        company: {
          entity: {},
          loading: false,
        },
      }
    case FETCH_COMPANIES:
      return {
        ...state,
        companies: {
          ...state.companies,
          loading: true,
        },
      }
    case FETCH_COMPANIES_SUCCESS:
      return {
        ...state,
        companies: {
          entities: action.entities,
          loading: false,
        },
      }
    case FETCH_COMPANIES_FAILURE:
      return {
        ...state,
        companies: {
          entities: {},
          loading: false,
        },
      }
    case UPDATE_COMPANY_SEARCH:
      return {
        ...state,
        search: action.params,
      }
    default:
      return state
  }
}

// Actions Creators
export const updateRequest = (payload) => ({
  type: UPDATE_REQUEST,
  payload: DataTransformer.transform(payload),
})

export const updateSuccess = (result) => ({
  type: UPDATE_SUCCESS,
  result,
})

export const updateFailure = (violations) => ({
  type: UPDATE_FAILURE,
  violations,
})

export const updateBankRequest = (payload) => ({
  type: UPDATE_BANK_REQUEST,
  payload: DataTransformer.transform(payload),
})

export const updateBankSuccess = (result) => ({
  type: UPDATE_BANK_SUCCESS,
  result,
})

export const updateBankFailure = (violations) => ({
  type: UPDATE_BANK_FAILURE,
  violations,
})

export const fetchCompany = (id) => ({
  type: FETCH_COMPANY,
  id,
})

export const fetchCompanySuccess = (entity) => ({
  type: FETCH_COMPANY_SUCCESS,
  entity,
})

export const fetchCompanyFailure = () => ({
  type: FETCH_COMPANY_FAILURE,
})

export const fetchCompanies = (params) => ({
  type: FETCH_COMPANIES,
  params,
})

export const updateCompanySearch = (params) => ({
  type: UPDATE_COMPANY_SEARCH,
  params,
})

export const fetchCompaniesSuccess = (entities) => ({
  type: FETCH_COMPANIES_SUCCESS,
  entities,
})

export const fetchCompaniesFailure = () => ({
  type: FETCH_COMPANIES_FAILURE,
})

// Epics
export const epic = combineEpics(
  updateRequestEpic,
  updateBankRequestEpic,
  refreshUserWhenUpdateCompanySuccessEpic,
  showAlertSuccessWhenUpdateCompanySuccessEpic,
  showAlertErrorWhenUpdateCompanyFailureEpic,
  sendAlertOnUpdateBankSuccessEpic,
  getCompanyEpic,
  getCompaniesEpic,
)

export function updateRequestEpic(action$) {
  return action$.ofType(UPDATE_REQUEST).mergeMap((action) =>
    Observable.of(Object.keys(action.payload).filter((key) => FIELDS.includes(key) || DOCUMENTS.includes(key)))
      .mergeMap((keys) =>
        Observable.fromPromise(
          Promise.all(
            keys.map((key) =>
              DOCUMENTS.includes(key) ? atob(action.payload[key]) : Promise.resolve(action.payload[key]),
            ),
          ),
        )
          .concatAll()
          .reduce((acc, curr, index) => ({ ...acc, [keys[index]]: curr }), {}),
      )
      .mergeMap((payload) => Observable.ajax(update({ payload, params: { id: action.payload.id } })))
      .map((res) => updateSuccess(res.response))
      // @ts-ignore
      .catch((err) => {
        if (typeof err === 'object' && (err.response || {}).code === 401) {
          return Observable.of(authenticationDuck.refreshTokenRequest(action))
        }

        try {
          const errors = [...(err.response.violations || []), ...(err.response.errors || [])].map((e) =>
            typeof e === 'string' ? e : e.message,
          )
          return Observable.of(updateFailure(errors))
        } catch (e) {
          return Observable.of(updateFailure(e.response?.violations))
        }
      }),
  )
}

export function updateBankRequestEpic(action$) {
  return action$.ofType(UPDATE_BANK_REQUEST).mergeMap((action) =>
    Observable.ajax(bank({ payload: action.payload }))
      .map((res) => updateBankSuccess(res.response))
      .catch((res) => Observable.of(updateBankFailure(res.response.violations))),
  )
}

export function sendAlertOnUpdateBankSuccessEpic(action$, store) {
  return action$.ofType(UPDATE_BANK_SUCCESS).mergeMap((action) =>
    ![TYPES.FREIGHT_FORWARDER, TYPES.MANAGER, TYPES.OPERATOR, TYPES.ACCOUNTANT].includes(
      (store.getState().authentication.payload || {}).type,
    ) || ['SUBMITTED', 'ACTIVE'].includes((action.result.managedCompany || {}).mangoMandateStatus)
      ? Observable.of(alertSuccess(intl.formatMessage({ id: 'ducks.bank.update.success' })))
      : Observable.of((action.result.managedCompany || {}).mangoMandateUrl)
          .filter((value) => value)
          .do((next) => (window.location.href = next))
          .mergeMap(() => Observable.never()),
  )
}

export function getCompanyEpic(action$) {
  return action$.ofType(FETCH_COMPANY).mergeMap((action) =>
    Observable.ajax(getCompany({ id: action.id }))
      .map((res) => fetchCompanySuccess(res.response))
      .catch((err) => {
        if (typeof err === 'object' && (err.response || {}).code === 401) {
          return Observable.of(authenticationDuck.refreshTokenRequest(action))
        }

        return Observable.of(fetchCompanyFailure())
      }),
  )
}

export function getCompaniesEpic(action$) {
  return action$.ofType(FETCH_COMPANIES).mergeMap((action) =>
    Observable.ajax(getCompanies({ params: action.params }))
      .map((res) => fetchCompaniesSuccess(res.response))
      .catch((err) => {
        if (typeof err === 'object' && (err.response || {}).code === 401) {
          return Observable.of(authenticationDuck.refreshTokenRequest(action))
        }

        return Observable.of(fetchCompaniesFailure())
      }),
  )
}

export function refreshUserWhenUpdateCompanySuccessEpic(action$) {
  return action$.ofType(UPDATE_SUCCESS).mapTo(fetchProfileRequest())
}

export function showAlertSuccessWhenUpdateCompanySuccessEpic(action$) {
  return action$.ofType(UPDATE_SUCCESS).mapTo(alertSuccess(intl.formatMessage({ id: 'ducks.company.update.success' })))
}

export function showAlertErrorWhenUpdateCompanyFailureEpic(action$) {
  return action$.ofType(UPDATE_FAILURE).mapTo(alertErrorDefault())
}

export const selectCompany = (state: GlobalState) => state.company.company.entity
export const selectCompanyLoading = (state: GlobalState) => state.company.company.loading
export const selectCompanies = (state: GlobalState) => state.company.companies.entities
export const selectCompaniesLoading = (state: GlobalState) => state.company.companies.loading
export const companyListener = createListenerMiddleware()

companyListener.startListening({
  predicate: (action, currentState: GlobalState, previousState: GlobalState) => {
    return currentState.company.search !== previousState.company.search
  },
  effect: async (action, listenerApi: ListenerEffectAPI<GlobalState, Dispatch>) => {
    listenerApi.cancelActiveListeners()
    await listenerApi.delay(500)

    const search = listenerApi.getState().company.search
    listenerApi.dispatch(fetchCompanies({ name: search }))
  },
})
